import { Link } from "react-router-dom";
import { Briefcase, ArrowUpRight } from "lucide-react";
import { motion } from "framer-motion";
import Reveal from "@/components/Reveal";
import { resolveAssetUrl } from "@/components/Logo";

const DEFAULT_IMG = "https://images.pexels.com/photos/3184360/pexels-photo-3184360.jpeg";

/**
 * Careers / "Working With Us" CTA section — inspired by dmciholdings.com.
 * All text is CMS-driven (props).
 */
export default function CareersBlock({
  overline = "Careers",
  titlePrefix = "Interested in",
  titleEmphasis = "working",
  titleSuffix = "with us",
  description = "",
  ctaLabel = "Apply Now",
  ctaLink = "/contact",
  imageUrl = "",
}) {
  const img = imageUrl ? resolveAssetUrl(imageUrl) : DEFAULT_IMG;

  return (
    <section className="relative overflow-hidden bg-emerald-ink">
      <div className="grid grid-cols-1 lg:grid-cols-2 min-h-[28rem]">
        <Reveal className="relative aspect-[16/10] lg:aspect-auto overflow-hidden">
          <motion.img
            src={img}
            alt="Our people"
            className="w-full h-full object-cover"
            initial={{ scale: 1.1 }}
            whileInView={{ scale: 1 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 1.4, ease: [0.16, 1, 0.3, 1] }}
            loading="lazy"
            decoding="async"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-transparent to-emerald-ink/30 lg:to-emerald-ink" />
        </Reveal>

        <div className="bg-emerald-ink text-bone relative flex items-center p-8 sm:p-12 lg:p-20">
          <div className="absolute inset-0 grid-bg opacity-50" />
          <Reveal delay={0.1} className="relative max-w-md">
            <Briefcase className="w-8 h-8 text-neon mb-6" />
            <div className="overline text-neon mb-4">{overline}</div>
            <h2 className="font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-[1.02]">
              {titlePrefix && <span className="gradient-text">{titlePrefix} </span>}
              {titleEmphasis && <span className="accent-italic">{titleEmphasis}</span>}
              {titleSuffix && <span className="gradient-text"> {titleSuffix}</span>}
              <span className="text-neon">?</span>
            </h2>
            {description && (
              <p className="mt-6 sm:mt-8 text-bone/65 text-base sm:text-lg leading-relaxed">
                {description}
              </p>
            )}
            <Link
              to={ctaLink || "/contact"}
              data-testid="careers-cta"
              className="btn-primary mt-8 sm:mt-10 group"
            >
              {ctaLabel}
              <ArrowUpRight className="w-4 h-4 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-transform" />
            </Link>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
